import { Card, Divider, notification, Space } from "antd";
import { useState } from "react";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import { getEnvironmentService } from "../../backend";
import { trackQuickDeploy } from "../../backend/tracking/deployment";
import { trackEnvComponentConfigured } from "../../backend/tracking/environment";
import ComponentConfigForm from "../../components/ComponentConfigForm";
import ExplanationButton from "../../components/explanations/ExplanationButton";
import ResourceConfigurationExplanation from "../../components/explanations/ResourceConfigurationExplanation";
import ChooseAwsAccountModal from "../../components/modals/ChooseAwsAccountModal";
import ConnectAwsAccountModal from "../../components/settings/ConnectAwsAccountModal";
import { ComponentConfig, IResourceDetails } from "../../types";
import { fetchAwsAccountsInfo, linkAwsAccountToStage } from "../../utils/aws-accounts";
import { useNotDeployedResourcesData } from "../../utils/environment/use-not-deployed-resources-data";

export default function ResourceSettingsGeneral() {
    const { projectId, envId } = useParams();
    const navigate = useNavigate();
    const { resourceDetails } = useOutletContext<{ resourceDetails: IResourceDetails }>();
    const [savingConfig, setSavingConfig] = useState(false);
    const [connectAwsAccountModalVisible, setConnectAwsAccountModalVisible] = useState(false);
    const [chooseAwsAccountModal, setChooseAwsAccountModal] = useState(false);
    const [awsAccountId, setAwsAccountId] = useState<string>();
    const [notDeployedResources, setNotDeployedResources] = useState<string[]>([]);

    useNotDeployedResourcesData({
        envId: envId!,
        projectId: projectId!,
        setNotDeployedResources
    });

    async function handleConfigChange(config: ComponentConfig[], shouldDeploy?: boolean) {
        try {
            setSavingConfig(true);
            await getEnvironmentService().updateResource(
                envId!,
                resourceDetails.name,
                projectId!,
                {
                    componentVersion: resourceDetails.version,
                    configurations: config.map(c => ({
                        key: c.key,
                        value: c.value,
                        reference: c.reference,
                        sensitive: c.sensitive
                    }))
                }
            );
            trackEnvComponentConfigured(
                envId!,
                envId!,
                resourceDetails.component.id,
                resourceDetails.version,
                resourceDetails.component.name
            );
            // Deploy config changes for this resource
            if (shouldDeploy) {
                deployStage();
            } else {
                notification.success({
                    message: "Resource saved",
                    description: "The changes in this resource have been saved and will be applied next time you initiate a deployment."
                });
            }
        } catch (error) {

        } finally {
            setSavingConfig(false);
        }
    }

    const deployStage = async () => {
        try {
            setSavingConfig(true);
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);
            if (stageAwsAccountId) {
                // AWS account is connected to both stage and project
                await getEnvironmentService().deployStage(
                    envId!,
                    projectId!,
                    {
                        partial: true,
                        resourceVersionOverrides: {
                            [resourceDetails.name]: resourceDetails.version
                        }
                    }
                );

                notification.success({
                    message: "Deployment initiated",
                    description: "It could take a few moments for the changes to take effect."
                });
                trackQuickDeploy(envId!);
                setTimeout(() => {
                    navigate(`/projects/${projectId}/environments/${envId}/deployments`);
                }, 2000);
            } else {
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (error: any) {
            notification.error({
                message: "Resource deployment failed",
                description: error?.response?.data?.message ? error.response.data.message : "We encountered an error while trying to deploy this resource. Please try again."
            });
        } finally {
            setSavingConfig(false);
        }
    }

    return (
        <>
            <Space direction="vertical" className="full-width">
                <Card bordered>
                    <Card.Meta
                        title={
                            <>
                                <span>Resource Configuration</span>
                                <ExplanationButton
                                    content={<ResourceConfigurationExplanation />}
                                />
                            </>
                        }
                        description={
                            <>
                                Configure resource properties. These variables are defined in the <code style={{ color: "var(--primary-color)" }}>schema.json</code> file in the repository.
                            </>
                        }
                    />
                    <Divider />
                    <ComponentConfigForm
                        schema={resourceDetails.component.schema}
                        values={resourceDetails.configurations}
                        loading={savingConfig}
                        resourceName={resourceDetails.name}
                        shouldValidateEksResource={
                            // check if resource is microtica-aws-eks(-spot) and if it's deployed
                            resourceDetails.component.id.includes("microtica-aws-eks") &&
                            !["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(resourceDetails.status)
                        }
                        onChange={handleConfigChange}
                        onActionClick={(config) => handleConfigChange(config, true)}
                        actions={[{ key: "save-deploy", value: "Save and Deploy" }]}
                        handleReferencedResources={{
                            shouldHandle: true,
                            notDeployedResources
                        }}
                    />
                </Card>
            </Space>
            {
                // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'connectAwsAccountModalVisible'
                // We have a process of periodic API calls which remain active even if when the modal closes
                connectAwsAccountModalVisible &&
                <ConnectAwsAccountModal
                    visible={connectAwsAccountModalVisible}
                    onOk={async (newAwsAccountId) => {
                        setConnectAwsAccountModalVisible(false);
                        setAwsAccountId(newAwsAccountId);
                        setChooseAwsAccountModal(true);
                    }}
                    onCancel={() => {
                        setConnectAwsAccountModalVisible(false);
                        setSavingConfig(false);
                    }}
                />
            }
            {
                // This modal becomes active when the user connects an AWS account and now needs to select a region for the stage
                chooseAwsAccountModal &&
                <ChooseAwsAccountModal
                    selectedAccount={{ accountId: awsAccountId, region: undefined }}
                    visible={chooseAwsAccountModal}
                    onCancel={() => setChooseAwsAccountModal(false)}
                    onOk={async ({ accountId, region }: { accountId: string, region: string }) => {
                        await linkAwsAccountToStage({
                            envId: envId!,
                            projectId: projectId!,
                            awsAccountId: accountId,
                            awsRegion: region
                        });
                        setChooseAwsAccountModal(false);
                        deployStage();
                    }}
                />
            }
        </>
    )
}